import {
  memo, useLayoutEffect, useMemo, useState,
} from 'react';
import { useStore, useViewport } from '@xyflow/react';

import { VALUE_CHAIN_MAP } from '../../../../../../constants/general';
import { VALUE_CHAIN_MAP_CUSTOM_NODES } from '../../../../../../constants/interfaces';

import type {
  IStickyColumnHeader,
  IStickyColumnHeaderNodeData,
  IStickyColumnHeaders,
} from '../interfaces/IStickyColumnHeaders';

import styles from '../sass/StickyColumnHeaders.module.scss';

// Fallbacks for when the floating top controls panel can't be measured
// (panel top: 100px / 20px in full screen, ~64px tall)
const STICKY_TOP_FALLBACK_OFFSET = 172;
const STICKY_TOP_FALLBACK_OFFSET_FULL_SCREEN = 92;
const STICKY_TOP_GAP = 8;
const COLUMN_BOTTOM_CLEARANCE = 40;
const TITLE_FONT_SIZE = 14;
const MIN_TITLE_WIDTH = 48;
const NEIGHBOUR_TITLES_GAP = 8;
const TOP_CONTROLS_PANEL_SELECTORS = '.react-flow__panel.top.center, .react-flow__panel.top.left';

function StickyColumnHeaders({ nodes, isFullScreenActive }: IStickyColumnHeaders) {
  const { x: viewportX, y: viewportY, zoom } = useViewport();
  const paneWidth = useStore((state) => state.width);
  const [stickyTopOffset, setStickyTopOffset] = useState(
    isFullScreenActive ? STICKY_TOP_FALLBACK_OFFSET_FULL_SCREEN : STICKY_TOP_FALLBACK_OFFSET,
  );

  useLayoutEffect(() => {
    const measureTopControlsPanel = () => {
      const flowElement = document.querySelector('.reactFlow');
      const topPanelElements = document.querySelectorAll(TOP_CONTROLS_PANEL_SELECTORS);

      if (!flowElement || !topPanelElements.length) {
        setStickyTopOffset(
          isFullScreenActive ? STICKY_TOP_FALLBACK_OFFSET_FULL_SCREEN : STICKY_TOP_FALLBACK_OFFSET,
        );
        return;
      }

      const flowTop = flowElement.getBoundingClientRect().top;
      let panelsBottom = 0;

      topPanelElements.forEach((panelElement) => {
        panelsBottom = Math.max(panelsBottom, panelElement.getBoundingClientRect().bottom - flowTop);
      });

      setStickyTopOffset(Math.max(STICKY_TOP_GAP, panelsBottom + STICKY_TOP_GAP));
    };

    measureTopControlsPanel();
    window.addEventListener('resize', measureTopControlsPanel);

    return () => {
      window.removeEventListener('resize', measureTopControlsPanel);
    };
  }, [isFullScreenActive]);

  const headers = useMemo<IStickyColumnHeader[]>(() => {
    if (!paneWidth || zoom <= 0) {
      return [];
    }

    return nodes.reduce<IStickyColumnHeader[]>((stickyHeaders, node) => {
      if (node.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.SWIMLANE || node.hidden) {
        return stickyHeaders;
      }

      const nodeData = node.data as unknown as IStickyColumnHeaderNodeData;
      const name = String(nodeData.name || '');

      if (!name) {
        return stickyHeaders;
      }

      const boardHeight = nodeData.accumulatedMaxBoardHeight || 0;
      const columnWidth = VALUE_CHAIN_MAP.SWIMLANE_WIDTH;
      const columnHeight = boardHeight - VALUE_CHAIN_MAP.TITLE_HEIGHT - VALUE_CHAIN_MAP.BOARD_PADDING;

      if (!columnWidth || columnHeight <= 0) {
        return stickyHeaders;
      }

      const screenLeft = node.position.x * zoom + viewportX;
      const screenWidth = columnWidth * zoom;
      const titleScreenTop = node.position.y * zoom + viewportY;
      const columnScreenBottom = (node.position.y + columnHeight) * zoom + viewportY;

      const isTitleScrolledAbove = titleScreenTop < stickyTopOffset;
      const isColumnStillOnScreen = columnScreenBottom > stickyTopOffset + COLUMN_BOTTOM_CLEARANCE;
      const isColumnHorizontallyVisible = screenLeft < paneWidth && screenLeft + screenWidth > 0;

      if (!isTitleScrolledAbove || !isColumnStillOnScreen || !isColumnHorizontallyVisible) {
        return stickyHeaders;
      }

      // Let the title overflow the column up to the neighbouring column edge
      const maxTitleWidth = (columnWidth + VALUE_CHAIN_MAP.SWIMLANE_GAP) * zoom;

      stickyHeaders.push({
        id: node.id,
        name,
        left: screenLeft,
        width: screenWidth,
        maxTitleWidth,
        fontSize: TITLE_FONT_SIZE,
      });

      return stickyHeaders;
    }, []).sort((firstHeader, secondHeader) => (
      (firstHeader.left + firstHeader.width / 2) - (secondHeader.left + secondHeader.width / 2)
    )).map((header, index, sortedHeaders) => {
      // Clamp the title at the midpoint to the neighbours so the pills never overlap
      const center = header.left + header.width / 2;
      const previousHeader = sortedHeaders[index - 1];
      const nextHeader = sortedHeaders[index + 1];
      let { maxTitleWidth } = header;

      if (previousHeader) {
        const previousCenter = previousHeader.left + previousHeader.width / 2;
        maxTitleWidth = Math.min(maxTitleWidth, center - previousCenter - NEIGHBOUR_TITLES_GAP);
      }

      if (nextHeader) {
        const nextCenter = nextHeader.left + nextHeader.width / 2;
        maxTitleWidth = Math.min(maxTitleWidth, nextCenter - center - NEIGHBOUR_TITLES_GAP);
      }

      maxTitleWidth = Math.max(maxTitleWidth, MIN_TITLE_WIDTH);

      return { ...header, maxTitleWidth, fontSize: TITLE_FONT_SIZE };
    });
  }, [nodes, paneWidth, stickyTopOffset, viewportX, viewportY, zoom]);

  if (!headers.length) {
    return null;
  }

  return (
    <div className={styles.container} aria-hidden="true">
      {headers.map((header) => (
        <div
          key={header.id}
          className={styles.header}
          style={{ left: header.left, width: header.width, top: stickyTopOffset }}
        >
          <span className={styles.title} style={{ fontSize: header.fontSize, maxWidth: header.maxTitleWidth }}>
            {header.name}
          </span>
        </div>
      ))}
    </div>
  );
}

export default memo(StickyColumnHeaders);
